import { DownOutlined } from '@ant-design/icons';
import { Popover, Typography } from 'antd';
import { useCallback, useState } from 'react';

import SearchTree from '../SearchTree';

import { DEFAULT_PLACEHOLDER } from './constants';
import * as Styled from './styles';
import type { Props } from './types';

const SearchTreePopover = ({
  title,
  currentValue,
  placeholder = DEFAULT_PLACEHOLDER,
  icon,
  width = 400,
  isLoading = false,
  onOpenChange,
  children,
  footer,
  searchValue: controlledSearchValue,
  onSearchValueChange,
  ...searchTreeProps
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const handleSearchValueChange = useCallback(
    (value: string) => {
      setSearchValue(value);
      onSearchValueChange?.(value);
    },
    [onSearchValueChange]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);

      if (!open) {
        handleSearchValueChange('');
      }
    },
    [onOpenChange, handleSearchValueChange]
  );

  const closePopover = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const renderContent = useCallback(
    (value: string) => children?.(value, closePopover),
    [children, closePopover]
  );

  const content = (
    <Styled.Panel $width={width}>
      <SearchTree
        {...searchTreeProps}
        isLoading={isLoading}
        searchValue={controlledSearchValue ?? searchValue}
        onSearchValueChange={handleSearchValueChange}
        renderContent={children ? renderContent : undefined}
      />
      {footer?.(closePopover)}
    </Styled.Panel>
  );

  return (
    <Popover
      content={content}
      trigger='click'
      placement='bottomLeft'
      open={isOpen}
      onOpenChange={handleOpenChange}
      align={{ offset: [15, 10] }}>
      <Styled.Trigger
        type='button'
        $hasIcon={Boolean(icon)}
        $isOpen={isOpen}
        aria-label={`${title}: ${currentValue ?? placeholder}`}>
        {icon && (
          <Styled.TriggerIcon $isOpen={isOpen}>{icon}</Styled.TriggerIcon>
        )}

        <Styled.TriggerLabel>
          <Typography.Text>{title}</Typography.Text>

          {isLoading ? (
            <Styled.LoadingLabel />
          ) : (
            <Typography.Text strong>
              {currentValue ?? placeholder}
            </Typography.Text>
          )}
        </Styled.TriggerLabel>

        <DownOutlined />
      </Styled.Trigger>
    </Popover>
  );
};

export default SearchTreePopover;
